import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { bgBlur } from 'src/theme/css';
import Iconify from 'src/components/iconify';
import { usePopover } from 'src/components/custom-popover';
import { useSnackbar } from 'src/components/snackbar';
import AccountPopover from 'src/layouts/_common/account-popover';
import LoginPopup from 'src/layouts/dashboard/LoginPopup';
import OrderHistory from 'src/layouts/dashboard/OrderHistory';
import { OV2 } from './ordering-v2-tokens';

// ----------------------------------------------------------------------

function parseLogoUrl(logo) {
  if (!logo) return null;
  if (typeof logo === 'string' && logo.includes('http')) return logo;
  return `https://syspos.s3.me-central-1.amazonaws.com/media/${logo}`;
}

function brandInitial(name) {
  if (!name) return 'W';
  const t = name.trim();
  return t ? t[0].toUpperCase() : 'W';
}

OrderingStoreHeader.propTypes = {
  location: PropTypes.object,
};

export default function OrderingStoreHeader({ location }) {
  const theme = useTheme();
  const loginPopup = usePopover();
  const orderHistoryPopover = usePopover();
  const guestAddressStub = usePopover();
  const { enqueueSnackbar } = useSnackbar();

  const name = location?.name || 'Restaurant';
  const logoSrc = parseLogoUrl(location?.logo_image);
  const phone = location?.phone || location?.contact_phone || '+971 4 000 0000';
  const subtitleParts = [`${name}`, '25 min', '★ 4.8'];

  const openRewards = useCallback(() => {
    enqueueSnackbar('Rewards coming soon', { variant: 'info' });
  }, [enqueueSnackbar]);

  return (
    <>
      <OrderHistory open={orderHistoryPopover.open} handleClose={orderHistoryPopover.onClose} />
      <LoginPopup open={loginPopup.open} handleClose={loginPopup.onClose} />

      <Box
        component="header"
        sx={{
          py: 2,
          minHeight: OV2.storeHeaderMinHeight,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          ...bgBlur({ color: alpha(theme.palette.background.paper, 0.82) }),
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{
            maxWidth: OV2.contentMaxWidth,
            mx: 'auto',
            px: { xs: 2, sm: 2.5, md: 3.5, lg: 4 },
            width: 1,
            gap: 2,
          }}
        >
        <Stack direction="row" alignItems="center" spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
          {logoSrc ? (
            <Box
              component="img"
              src={logoSrc}
              alt=""
              sx={{ width: 48, height: 48, borderRadius: OV2.radiusSm, objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: OV2.radiusSm,
                background: `linear-gradient(135deg, ${OV2.primaryDark} 0%, ${OV2.primaryBright} 100%)`,
                color: '#fff',
                fontWeight: 800,
                fontSize: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: `0 2px 8px ${alpha(OV2.primary, 0.35)}`,
              }}
            >
              {brandInitial(name)}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              {name}
            </Typography>
            <Typography variant="body2" sx={{ color: OV2.textSecondary, fontSize: 13, mt: 0.25 }}>
              {subtitleParts.join(' • ')}
            </Typography>
          </Box>
        </Stack>

        <Stack alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' }, flexShrink: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 18 }}>
            {phone}
          </Typography>
          <Typography variant="caption" sx={{ color: OV2.textSecondary }}>
            Call us
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }} sx={{ flex: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Rewards">
            <Box
              component="button"
              type="button"
              onClick={openRewards}
              aria-label="Rewards"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.35,
                cursor: 'pointer',
                border: 'none',
                bgcolor: 'transparent',
                p: 0.5,
                borderRadius: 1.5,
                '&:hover': { bgcolor: (t) => alpha(t.palette.grey[500], 0.08) },
              }}
            >
              <Iconify icon="solar:gift-linear" width={22} sx={{ color: OV2.primary }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: OV2.textSecondary,
                  lineHeight: 1.2,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Rewards
              </Typography>
            </Box>
          </Tooltip>
          <AccountPopover
            orderingV2Trigger
            openOrderHistory={orderHistoryPopover.onOpen}
            openAddress={guestAddressStub.onOpen}
            openLogin={loginPopup.onOpen}
          />
        </Stack>
        </Stack>
      </Box>
    </>
  );
}
